import { CalendarOutlined, CopyOutlined, DownOutlined, EllipsisOutlined, MailOutlined, MessageOutlined, PhoneOutlined, SearchOutlined, UpOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Card, Col, DatePicker, Dropdown, Input, message, Modal, Row, Space, Statistic, Table } from 'antd';
import { Calendar, Phone, UserCircle, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import CVModal from '../components/CV/CVModal';
import SendCVModal from '../components/CV/SendCVModal';
import RecordingPlayer from '../components/Recording/RecordingPlayer';
import StatusIndicator from '../components/ResponseMonitor/StatusIndicator';
import { DataCard, MetricCard } from '../metronic/components';
import MetronicLayout from '../metronic/layouts/MetronicLayout';
import { contactsApi } from '../services/contactsApi';

// Conversation Transcript Component
const ConversationTranscript = ({ text }) => {
  const [expandedMessages, setExpandedMessages] = useState(new Set());

  // Parse transcript into conversation turns
  const parseTranscript = (transcriptText) => {
    const lines = transcriptText.split('\n').filter(line => line.trim());
    const messages = [];
    let currentSpeaker = null;
    let currentMessage = '';

    for (const line of lines) {
      // Check for speaker indicators
      if (/^(AI|AI Agent|Agent|Alice):?/i.test(line)) {
        if (currentSpeaker && currentMessage.trim()) {
          messages.push({ speaker: currentSpeaker, message: currentMessage.trim() });
        }
        currentSpeaker = 'AI Agent';
        currentMessage = line.replace(/^(AI|AI Agent|Agent|Alice):?\s*/i, '');
      } else if (/^(User|Candidate|Caller):?/i.test(line)) {
        if (currentSpeaker && currentMessage.trim()) {
          messages.push({ speaker: currentSpeaker, message: currentMessage.trim() });
        }
        currentSpeaker = 'User';
        currentMessage = line.replace(/^(User|Candidate|Caller):?\s*/i, '');
      } else if (line.match(/^\d{1,2}:\d{2}/)) {
        // Timestamp line - skip
        continue;
      } else {
        // Continuation of current message
        if (currentSpeaker) {
          currentMessage += (currentMessage ? ' ' : '') + line;
        }
      }
    }

    // Add the last message
    if (currentSpeaker && currentMessage.trim()) {
      messages.push({ speaker: currentSpeaker, message: currentMessage.trim() });
    }

    return messages;
  };

  const messages = parseTranscript(text);

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedMessages(newExpanded);
  };

  const isExpanded = (index) => expandedMessages.has(index);

  return (
    <div className="space-y-4">
      {messages.map((msg, index) => {
        const isAI = msg.speaker === 'AI Agent';
        const isLongMessage = msg.message.length > 200;
        const shouldShowExpand = isLongMessage && !isExpanded(index);
        const displayMessage = shouldShowExpand
          ? msg.message.substring(0, 200) + '...'
          : msg.message;

        return (
          <div key={index} className={`flex ${isAI ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${
              isAI
                ? 'bg-blue-500 text-white rounded-lg rounded-tl-none'
                : 'bg-gray-200 text-gray-800 rounded-lg rounded-tr-none'
            } p-3 shadow-sm`}>
              <div className="flex items-center mb-1">
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  isAI ? 'bg-blue-300' : 'bg-gray-400'
                }`} />
                <span className="text-xs font-medium opacity-75">
                  {isAI ? '🤖 AI Agent' : '👤 User'}
                </span>
              </div>
              <div className="text-sm leading-relaxed">
                <p className="whitespace-pre-wrap">{displayMessage}</p>
                {shouldShowExpand && (
                  <Button
                    type="text"
                    size="small"
                    icon={<DownOutlined />}
                    onClick={() => toggleExpanded(index)}
                    className="mt-2 p-0 h-auto text-xs"
                    style={{ color: isAI ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)' }}
                  >
                    Show more
                  </Button>
                )}
                {isExpanded(index) && (
                  <Button
                    type="text"
                    size="small"
                    icon={<UpOutlined />}
                    onClick={() => toggleExpanded(index)}
                    className="mt-2 p-0 h-auto text-xs"
                    style={{ color: isAI ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)' }}
                  >
                    Show less
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transcriptData, setTranscriptData] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [sendCVData, setSendCVData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [whatsappData, setWhatsappData] = useState(null);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  const [summary, setSummary] = useState({
    totalContacts: 0,
    contactedCount: 0,
    recentContactedCount: 0,
    notContactedCount: 0
  });

  // Function to strip HTML tags and return plain text
  const stripHtml = (html) => {
    if (!html) return '';

    // Create a temporary div element to parse HTML
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;

    // Get text content and clean up whitespace
    const text = tmp.textContent || tmp.innerText || '';
    return text.trim();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchContacts = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format('YYYY-MM-DD');
        params.date_to = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await contactsApi.getContacts(params);

      if (response.success && Array.isArray(response.contacts)) {
        setContacts(response.contacts);
        setPagination(prev => ({
          ...prev,
          current: response?.pagination?.page || page,
          pageSize: response?.pagination?.page_size || pageSize,
          total: response?.pagination?.total || 0
        }));
        if (response.summary) {
          setSummary(response.summary);
        }
      } else {
        message.error('Invalid data format received');
        setContacts([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      message.error('Failed to fetch contacts');
      console.error('Error:', error);
      setContacts([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, debouncedSearch, dateRange]);

  const handleViewTranscript = async (contact) => {
    try {
      console.log('Full Contact Data:', JSON.stringify(contact, null, 2));

      // Get call history directly from the correct path
      const callHistory = contact?.communication?.calls?.history || [];
      console.log('Call History Structure:', {
        path: 'communication.calls.history',
        rawHistory: callHistory,
        historyLength: callHistory.length,
        firstCall: callHistory[0] // Log first call if exists
      });

      // No additional filtering needed since backend already filters for recordingUrl
      const latestCall = callHistory.length > 0
        ? callHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        : null;

      console.log('Latest Call:', latestCall);

      if (latestCall) {
        // Fetch job details for the latest call
        const jobDetails = contact.lastEvaluatedJob?.id === latestCall.jobId
          ? contact.lastEvaluatedJob
          : { title: `Job ID: ${latestCall.jobId}` };

        setTranscriptData({
          text: latestCall.transcription,
          audioUrl: latestCall.recordingUrl,
          metadata: {
            duration: latestCall.duration,
            status: latestCall.status,
            jobId: latestCall.jobId,
            jobTitle: jobDetails.title,
            timestamp: latestCall.timestamp,
            notes: latestCall.notes,
            type: latestCall.type
          },
          // Previous calls (excluding latest)
          previousCalls: callHistory
            .filter(call => call.timestamp !== latestCall.timestamp)
            .map(call => ({
              recordingUrl: call.recordingUrl,
              transcription: call.transcription,
              timestamp: call.timestamp,
              status: call.status,
              jobId: call.jobId,
              jobTitle: contact.lastEvaluatedJob?.id === call.jobId
                ? contact.lastEvaluatedJob.title
                : `Job ID: ${call.jobId}`,
              duration: call.duration,
              notes: call.notes,
              type: call.type,
              is_followup: call.is_followup,
              followup_attempt: call.followup_attempt,
              original_end_reason: call.original_end_reason
            }))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        });
      } else {
        console.log('No calls found in history. Contact data:', {
          hasHistory: !!contact?.communication?.calls?.history,
          historyLength: callHistory.length
        });
        message.info('No recordings available');
      }
    } catch (error) {
      console.error('Error handling transcript view:', error);
      console.error('Error details:', {
        contactExists: !!contact,
        communicationExists: !!contact?.communication,
        callsExists: !!contact?.communication?.calls,
        errorMessage: error.message
      });
      message.error('Failed to load transcript data');
    }
  };

  const handleViewCV = async (contact) => {
    try {
      console.log('Viewing CV for:', contact.contactName);
      const contactId = contact.id || contact._id || contact.documentId;
      if (!contactId) {
        message.error('Contact ID not found');
        return;
      }

      const cvResponse = await contactsApi.getContactCV(contactId);
      const fullText = cvResponse?.contact?.fullText || '';

      if (fullText) {
        setCvData({
          // Required for backend send-cv
          id: contactId,
          // Display / email metadata
          text: fullText,
          contactName: cvResponse?.contact?.contactName || contact.contactName,
          title: cvResponse?.contact?.title || contact.title,
          email: cvResponse?.contact?.email || contact.email,
          phone: contact.phone,
          workExperience: cvResponse?.contact?.workExperience || [],
          linkedinUrl: cvResponse?.contact?.linkedinUrl || '',
          skills: cvResponse?.contact?.skills || [],
          rating: contact.rating || null,
          lastEvaluatedJob: cvResponse?.contact?.lastEvaluatedJob || contact.lastEvaluatedJob || null,
          evaluationSummary: cvResponse?.contact?.evaluationSummary || (contact.lastEvaluatedJob && contact.lastEvaluatedJob.evaluationSummary) || contact.evaluationSummary || null
        });
      } else {
        message.info('No CV text available');
      }
    } catch (error) {
      console.error('Error viewing CV:', error);
      message.error('Failed to load CV');
    }
  };

  const handleViewStats = async (contact) => {
    try {
      console.log('Viewing stats for:', contact.contactName);

      // Get the contact ID - it could be in different formats
      const contactId = contact.id || contact._id || contact.documentId;

      if (!contactId) {
        message.error('Contact ID not found');
        return;
      }

      // Always fetch from API for the latest stats
      const statsData = await contactsApi.getContactStats(contactId);
      console.log('Stats Data fetched from API:', statsData);

      // Debug: Log call history structure
      if (statsData.callHistory) {
        console.log('📞 Call History Count:', statsData.callHistory.length);
        console.log('📊 First Call Sample:', statsData.callHistory[0]);

        // Count transcriptions
        const withTranscript = statsData.callHistory.filter(c => c.transcription).length;
        const withoutTranscript = statsData.callHistory.length - withTranscript;
        console.log(`✅ Calls with transcription: ${withTranscript}`);
        console.log(`❌ Calls without transcription: ${withoutTranscript}`);

        // Show sample of call without transcription
        const noTranscriptCall = statsData.callHistory.find(c => !c.transcription);
        if (noTranscriptCall) {
          console.log('📝 Sample call WITHOUT transcript:', {
            timestamp: noTranscriptCall.timestamp,
            status: noTranscriptCall.status,
            duration: noTranscriptCall.duration,
            fields: Object.keys(noTranscriptCall)
          });
        }

        // Show sample of call with transcription
        const withTranscriptCall = statsData.callHistory.find(c => c.transcription);
        if (withTranscriptCall) {
          console.log('📝 Sample call WITH transcript:', {
            timestamp: withTranscriptCall.timestamp,
            status: withTranscriptCall.status,
            duration: withTranscriptCall.duration,
            transcriptionLength: withTranscriptCall.transcription?.length,
            fields: Object.keys(withTranscriptCall)
          });
        }
      }

      if (statsData.success) {
        setStatsData({
          contact,
          stats: statsData
        });
      } else {
        message.error('Failed to fetch stats: ' + (statsData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error viewing stats:', error);
      message.error('Failed to load stats: ' + (error.message || 'Unknown error'));
    }
  };

  const handleViewEmails = (emailHistory) => {
    setEmailPreviewData({
      contact: statsData.contact,
      emails: emailHistory
    });
  };

  const handleViewWhatsApp = async (contact) => {
    try {
      console.log('Viewing WhatsApp conversation for:', contact.contactName);

      // Get the contact ID - it could be in different formats
      const contactId = contact.id || contact._id || contact.documentId;

      if (!contactId) {
        message.error('Contact ID not found');
        return;
      }

      // Fetch WhatsApp conversation from API
      const whatsappData = await contactsApi.getWhatsAppConversation(contactId);
      console.log('WhatsApp Data fetched from API:', whatsappData);

      if (whatsappData.success) {
        setWhatsappData({
          contact,
          conversation: whatsappData
        });
      } else {
        message.error('Failed to fetch WhatsApp conversation: ' + (whatsappData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error viewing WhatsApp conversation:', error);
      message.error('Failed to load WhatsApp conversation: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: 'CONTACT NAME',
      dataIndex: 'contactName',
      render: (text, record) => (
        <Space>
          <Avatar
            src={record.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${text}`}
            alt={text}
            icon={<UserOutlined />}
          />
          <div>
            <div>{text}</div>
            <div style={{ color: '#666' }}>{record.currentTitle || record.title || 'No Title'}</div>
          </div>
        </Space>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search contact name"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.contactName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'CONTACT',
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <div>{record.phone}</div>
        </div>
      ),
    },
    {
      title: 'LAST EVALUATED JOB',
      render: (_, record) => {
        const lastJob = record.lastEvaluatedJob;

        // Function to clean and format job title
        const formatJobTitle = (title) => {
          if (!title) return 'Unknown';

          // Remove the path prefix
          const cleanTitle = title.replace('job_descriptions/Job_', '')
            .replace('job_descriptions/job_', '');

          // Replace underscores and dashes with spaces
          return cleanTitle
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
            .replace(/-/g, ' ')
            .trim();
        };

        return lastJob ? (
          <div>
            <div>{formatJobTitle(lastJob.title)}</div>
            <div className="text-gray-400">{lastJob.company || 'Unknown Company'}</div>
            <div className="text-gray-400">Score: {lastJob.score}</div>
          </div>
        ) : (
          <div>No evaluated jobs</div>
        );
      },
    },
    {
      title: 'LAST INTERACTION',
      render: (_, record) => {
        // Get stats from communication object
        const stats = record.communication?.stats;

        // Format date function
        const formatDate = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return new Intl.DateTimeFormat('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).format(date);
        };

        return (
          <div>
            <div>Status: {record.status}</div>
            <div className="text-gray-400">
              Total: {stats?.totalInteractions || 0} interactions
            </div>
            {stats?.lastContactDate && (
              <div className="text-gray-400">
                Last: {formatDate(stats.lastContactDate)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusIndicator status={status} />,
      filters: [
        { text: 'Not Contacted', value: 'Not Contacted' },
        { text: 'Contacted', value: 'Contacted' },
        { text: 'Latest Contacted', value: 'Latest Contacted' }
      ],
      onFilter: (value, record) => {
        if (value === 'Latest Contacted') {
          // Check if the contact was contacted in the last 7 days
          const lastContactDate = record.communication?.stats?.lastContactDate;
          if (!lastContactDate) return false;

          const contactDate = new Date(lastContactDate);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

          return contactDate >= sevenDaysAgo;
        }
        return record.status === value;
      },
    },
    {
      title: 'ACTION',
      render: (_, record) => {
        // Menu items for dropdown
        const menuItems = [
          {
            key: 'transcript',
            label: 'View Transcript',
            icon: <PhoneOutlined />,
            onClick: () => handleViewTranscript(record),
          },
          {
            key: 'cv',
            label: 'View CV',
            icon: <UserOutlined />,
            onClick: () => handleViewCV(record),
          },
          {
            key: 'stats',
            label: 'View Stats',
            icon: <CalendarOutlined />,
            onClick: () => handleViewStats(record),
          },
          {
            key: 'whatsapp',
            label: 'View WhatsApp',
            icon: <MessageOutlined />,
            onClick: () => handleViewWhatsApp(record),
          },
        ];

        return (
          <Space>
            {/* Primary Actions - Show inline */}
            <Button
              type={record.status === 'Contacted' ? 'primary' : 'default'}
              style={{
                backgroundColor: record.status === 'Contacted' ? '#10b981' : undefined,
                borderColor: record.status === 'Contacted' ? '#10b981' : undefined,
              }}
            >
              {record.status}
            </Button>
            <Button onClick={() => handleViewTranscript(record)}>View Transcript</Button>
            <Button onClick={() => handleViewCV(record)}>View CV</Button>
            <Button onClick={() => handleViewStats(record)}>View Stats</Button>

            {/* More Options Dropdown */}
            <Dropdown
              menu={{
                items: menuItems,
              }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                icon={<EllipsisOutlined />}
                style={{
                  border: 'none',
                  boxShadow: 'none',
                }}
              />
            </Dropdown>
          </Space>
        );
      },
    }
  ];

  return (
    <MetronicLayout>
      <div className="p-6">
        {/* Statistics Cards - Metronic */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Total Contacts"
            value={summary.totalContacts}
            icon={Users}
            variant="primary"
          />
          <MetricCard
            title="Contacted"
            value={summary.contactedCount}
            icon={Phone}
            variant="success"
          />
          <MetricCard
            title="Recent (7 days)"
            value={summary.recentContactedCount}
            icon={Calendar}
            variant="info"
          />
          <MetricCard
            title="Not Contacted"
            value={summary.notContactedCount}
            icon={UserCircle}
            variant="warning"
          />
        </div>

        {/* Search & Filter Bar - Metronic DataCard */}
        <DataCard
          title="Search & Filter"
          className="mb-6"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Input
                placeholder="Search by contact name"
                prefix={<SearchOutlined />}
                onChange={e => handleSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={12}>
              <DatePicker.RangePicker
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
                placeholder={['Filter by last contact date', '']}
              />
            </Col>
          </Row>
        </DataCard>

        {/* Contacts Table - Metronic DataCard */}
        <DataCard
          title="Contact Database"
          subtitle={`${pagination.total} contacts`}
          loading={loading}
        >
          <Table
            columns={columns}
            dataSource={contacts}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || prev.pageSize
                }));
              }
            }}
          />
        </DataCard>

        {/* Transcript Modal */}
        <Modal
          title="Call Recordings & Transcript"
          open={transcriptData !== null}
          onCancel={() => setTranscriptData(null)}
          width={800}
          footer={null}
        >
          {transcriptData && (
            <div className="space-y-6">
              {/* Latest Recording Section */}
              {transcriptData.audioUrl && (
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-2">Latest Recording</h3>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">
                      <p className="font-medium">Job ID: {transcriptData.metadata.jobId}</p>
                      <p className="text-gray-500">Title: {transcriptData.metadata.jobTitle || 'Unknown Position'}</p>
                      <p>Date: {new Date(transcriptData.metadata.timestamp).toLocaleString()}</p>
                      <p>Duration: {transcriptData.metadata.duration}</p>
                      <p>Status: {transcriptData.metadata.status}</p>
                    </div>
                    <RecordingPlayer audioUrl={transcriptData.audioUrl} />
                    {transcriptData.text && (
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium">Conversation Transcript</h4>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(transcriptData.text);
                              message.success('Transcript copied to clipboard');
                            }}
                          >
                            Copy Transcript
                          </Button>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                          <ConversationTranscript text={transcriptData.text} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Previous Recordings Section */}
              {transcriptData.previousCalls?.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">Previous Recordings</h3>
                  <div className="space-y-4">
                    {transcriptData.previousCalls.map((call, index) => (
                      <div key={index} className="border-b pb-4 last:border-b-0">
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-sm text-gray-600">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium">Job ID: {call.jobId}</p>
                              {call.is_followup && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300">
                                  🔄 Follow-up #{call.followup_attempt}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-500">Title: {call.jobTitle || 'Unknown Position'}</p>
                            <p>Date: {new Date(call.timestamp).toLocaleString()}</p>
                            <p>Duration: {call.duration}</p>
                            <p>Status: {call.status}</p>
                            {call.is_followup && call.original_end_reason && (
                              <p className="text-orange-600 text-xs mt-1">
                                Original: {call.original_end_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <RecordingPlayer audioUrl={call.recordingUrl} />
                        {call.transcription && (
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-medium">Conversation Transcript</h4>
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(call.transcription);
                                  message.success('Transcript copied to clipboard');
                                }}
                              >
                                Copy Transcript
                              </Button>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                              <ConversationTranscript text={call.transcription} />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Stats Modal */}
        <Modal
          title="Communication Statistics"
          open={statsData !== null}
          onCancel={() => setStatsData(null)}
          width={800}
          footer={null}
        >
          {statsData && (
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Contact Name"
                        value={statsData.contact.contactName}
                        prefix={<UserOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Current Title"
                        value={statsData.contact.currentTitle || statsData.contact.title || 'No Title'}
                      />
                    </Card>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Email"
                        value={statsData.contact.email}
                        prefix={<MailOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Phone"
                        value={String(statsData.contact.phone)}
                        prefix={<PhoneOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Communication Statistics</h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Total Calls"
                        value={statsData.stats.totalCalls || 0}
                        prefix={<PhoneOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Total Emails"
                        value={statsData.stats.totalEmails || 0}
                        prefix={<MailOutlined />}
                        valueStyle={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => {
                          if (statsData.stats.emailHistory && statsData.stats.emailHistory.length > 0) {
                            handleViewEmails(statsData.stats.emailHistory);
                          } else {
                            message.info('No email history available');
                          }
                        }}
                      />
                    </Card>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Total Messages"
                        value={statsData.stats.totalMessages || 0}
                        prefix={<MessageOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Total Interactions"
                        value={statsData.stats.totalInteractions || 0}
                        prefix={<UserOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Last Contacted"
                        value={statsData.stats.lastContactDate ? new Date(statsData.stats.lastContactDate).toLocaleString() : 'Never'}
                        prefix={<CalendarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">📧 Email Opens</div>
                        <div className="text-2xl font-bold text-purple-600">
                          {statsData.stats.emailHistory ?
                            statsData.stats.emailHistory.filter(e => e.eventType === 'open' || e.status === 'opened').length : 0}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          of {statsData.stats.totalEmails || 0} sent
                        </div>
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">✔✔ Messages Read</div>
                        <div className="text-2xl font-bold text-green-600">
                          {statsData.stats.whatsappHistory ?
                            statsData.stats.whatsappHistory.filter(w => w.status === 'read' && w.direction === 'outbound').length : 0}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          of {statsData.stats.whatsappHistory ?
                            statsData.stats.whatsappHistory.filter(w => w.direction === 'outbound').length : 0} sent
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>

              {statsData.stats.callHistory && statsData.stats.callHistory.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Call History</h3>
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        const callHistoryText = statsData.stats.callHistory
                          .map((call, idx) => {
                            const date = new Date(call.timestamp).toLocaleString();
                            const duration = call.duration || 'N/A';
                            const status = call.status || 'N/A';
                            const endReason = call.end_reason || 'N/A';
                            const callId = call.call_id || 'N/A';
                            const type = call.type || 'N/A';
                            const transcript = call.transcription || 'No transcript available';
                            const notes = call.notes || 'No notes';

                            return `Call #${idx + 1}
Date: ${date}
Duration: ${duration}
Status: ${status}
Type: ${type}
End Reason: ${endReason}
Call ID: ${callId}

Transcript:
${transcript}

Notes:
${notes}

---
`;
                          })
                          .join('\n');

                        navigator.clipboard.writeText(callHistoryText);
                        message.success('Call history copied to clipboard');
                      }}
                    >
                      Copy call history
                    </Button>
                  </div>
                  <div className="space-y-6 max-h-96 overflow-y-auto">
                    {statsData.stats.callHistory.map((call, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                        {/* Call Header */}
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="bg-blue-100 p-2 rounded-full">
                              <PhoneOutlined className="text-blue-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-800">
                                  Call #{index + 1}
                                </h4>
                                {call.is_followup && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300">
                                    🔄 Follow-up #{call.followup_attempt}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">
                                {new Date(call.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                call.status === 'completed' ? 'bg-green-100 text-green-800' :
                                call.status === 'missed' ? 'bg-red-100 text-red-800' :
                                call.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {call.status || 'Unknown'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {call.duration || 'N/A'} min
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Follow-up Info Banner */}
                        {call.is_followup && (
                          <div className="bg-orange-50 border-l-4 border-orange-400 rounded-lg p-3 mb-3">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <span className="text-orange-600 font-semibold text-xs">🔄 FOLLOW-UP CALL</span>
                              </div>
                              <div className="ml-3 flex-1">
                                <p className="text-sm text-gray-700">
                                  This is follow-up attempt <strong>#{call.followup_attempt}</strong>.
                                  {call.original_end_reason && (
                                    <span> Original call ended: <strong>{call.original_end_reason}</strong></span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Call Details */}
                        <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Duration:</span>
                            <span className="ml-2 text-gray-800">{call.duration || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">End Reason:</span>
                            <span className="ml-2 text-gray-800">{call.end_reason || 'N/A'}</span>
                          </div>
                          {call.type && (
                            <div>
                              <span className="font-medium text-gray-600">Type:</span>
                              <span className="ml-2 text-gray-800">{call.type}</span>
                            </div>
                          )}
                          {call.call_id && (
                            <div>
                              <span className="font-medium text-gray-600">Call ID:</span>
                              <span className="ml-2 text-gray-800 font-mono text-xs">{call.call_id}</span>
                            </div>
                          )}
                        </div>

                        {/* Summary Section (if available) */}
                        {call.summary && (
                          <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-400 mb-3">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <span className="text-blue-600 font-semibold text-xs">📝 SUMMARY</span>
                              </div>
                              <div className="ml-3 flex-1">
                                <p className="text-sm text-gray-700 italic">
                                  {call.summary}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Transcript Section */}
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex justify-between items-center mb-2">
                            <h5 className="font-medium text-gray-700">Full Transcript</h5>
                            {call.transcription && (
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(call.transcription);
                                  message.success('Transcript copied to clipboard');
                                }}
                              >
                                Copy
                              </Button>
                            )}
                          </div>
                          {call.transcription ? (
                            <div className="bg-gray-50 rounded p-3 max-h-64 overflow-y-auto">
                              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {call.transcription}
                              </p>
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <p className="text-xs text-gray-500">
                                  Length: {call.transcription.length} characters
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-yellow-50 rounded p-3 text-center">
                              <p className="text-sm text-gray-600">
                                No transcript available for this call
                              </p>
                              {call.recordingUrl && (
                                <p className="text-xs text-gray-500 mt-1">
                                  (Recording available below)
                                </p>
                              )}
                              {!call.recordingUrl && call.status !== 'completed' && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Call status: {call.status}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                                                {/* Recording Section */}
                        {call.recordingUrl && (
                          <div className="mt-3">
                            <div className="flex justify-between items-center mb-2">
                              <h5 className="font-medium text-gray-700">Recording</h5>
                            </div>
                            <div className="bg-gray-50 rounded p-3">
                              <RecordingPlayer audioUrl={call.recordingUrl} />
                            </div>
                          </div>
                        )}

                        {/* Notes Section */}
                        {call.notes && (
                          <div className="mt-3">
                            <h5 className="font-medium text-gray-700 mb-2">Notes</h5>
                            <div className="bg-yellow-50 rounded p-3 border-l-4 border-yellow-400">
                              <p className="text-sm text-gray-700">{call.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {statsData.stats.emailHistory && statsData.stats.emailHistory.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Email History</h3>
                    <Button
                      type="primary"
                      icon={<MailOutlined />}
                      onClick={() => handleViewEmails(statsData.stats.emailHistory)}
                    >
                      View All Emails ({statsData.stats.emailHistory.length})
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {statsData.stats.emailHistory.slice(0, 3).map((email, index) => {
                      // Determine read status
                      const isOpened = email.eventType === 'open' || email.status === 'opened';
                      const isClicked = email.eventType === 'click' || email.status === 'clicked';
                      const isDelivered = email.eventType === 'delivered' || email.status === 'delivered';
                      const isSent = email.eventType === 'sent' || email.status === 'sent';

                      return (
                        <div key={index} className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                             onClick={() => handleViewEmails([email])}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-gray-800">
                                  {email.subject || 'No Subject'}
                                </div>
                                {/* Visual Read Receipt Indicators */}
                                {isClicked && (
                                  <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full flex items-center gap-1">
                                    🔗 Clicked
                                  </span>
                                )}
                                {isOpened && !isClicked && (
                                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                                    👁️ Opened
                                  </span>
                                )}
                                {isDelivered && !isOpened && !isClicked && (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                                    ✔ Delivered
                                  </span>
                                )}
                                {isSent && !isDelivered && !isOpened && !isClicked && (
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full flex items-center gap-1">
                                    ➤ Sent
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">
                                To: {email.recipient || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {email.timestamp ? new Date(email.timestamp).toLocaleString() : 'Unknown date'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {statsData.stats.emailHistory.length > 3 && (
                      <div className="text-center text-gray-500 text-sm">
                        ... and {statsData.stats.emailHistory.length - 3} more emails
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        <CVModal
          cvData={cvData}
          isOpen={cvData !== null}
          onClose={() => setCvData(null)}
          onSendCV={(data) => setSendCVData(data)}
        />

        <SendCVModal
          data={sendCVData}
          isOpen={sendCVData !== null}
          onClose={() => setSendCVData(null)}
        />

        {/* WhatsApp Conversation Modal */}
        <Modal
          title="WhatsApp Conversation"
          open={whatsappData !== null}
          onCancel={() => setWhatsappData(null)}
          width={800}
          footer={null}
        >
          {whatsappData && (
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Contact Name"
                        value={whatsappData.contact.contactName}
                        prefix={<UserOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Phone Numbers"
                        value={whatsappData.conversation.phoneNumbers?.join(', ') || 'No phone numbers'}
                        prefix={<PhoneOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Conversation Statistics</h3>
                <Row gutter={16}>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Total Messages"
                        value={whatsappData.conversation.totalMessages || 0}
                        prefix={<MessageOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Inbound Messages"
                        value={whatsappData.conversation.inboundMessages || 0}
                        prefix={<UserOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Outbound Messages"
                        value={whatsappData.conversation.outboundMessages || 0}
                        prefix={<UserOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>

              {whatsappData.conversation.conversationState && (
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">Conversation State</h3>
                  <div className="space-y-2">
                    <p><strong>Current State:</strong> {whatsappData.conversation.conversationState.state || 'Unknown'}</p>
                    <p><strong>Message Count:</strong> {whatsappData.conversation.conversationState.message_count || 0}</p>
                    <p><strong>Last Message:</strong> {whatsappData.conversation.conversationState.last_message_at ? new Date(whatsappData.conversation.conversationState.last_message_at).toLocaleString() : 'Never'}</p>
                    <p><strong>Created:</strong> {whatsappData.conversation.conversationState.created_at ? new Date(whatsappData.conversation.conversationState.created_at).toLocaleString() : 'Unknown'}</p>
                  </div>
                </div>
              )}

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Message History</h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {whatsappData.conversation.messages && whatsappData.conversation.messages.length > 0 ? (
                    // Messages are already sorted chronologically from backend
                    whatsappData.conversation.messages.map((message, index) => {
                        const isInbound = message.direction === 'inbound';
                        const hasIntent = message.intent || message.sentiment || message.confidence;

                        return (
                          <div
                            key={message.messageId || index}
                            className={`p-3 rounded-lg ${
                              isInbound
                                ? 'bg-blue-50 border-l-4 border-blue-400'
                                : 'bg-green-50 border-l-4 border-green-400'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm">
                                    {isInbound ? '📥 Received from Candidate' : '📤 Sent by ALICE'}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-2">
                                  {message.timestamp ? new Date(message.timestamp).toLocaleString() : 'Unknown time'}
                                </p>

                                {/* Status for outbound messages */}
                                {!isInbound && message.status && (
                                  <p className="text-xs mb-2">
                                    <span className="font-medium">Status: </span>
                                    <span className={
                                      message.status === 'read' ? 'text-green-600' :
                                      message.status === 'delivered' ? 'text-blue-600' :
                                      message.status === 'failed' ? 'text-red-600' :
                                      'text-gray-600'
                                    }>
                                      {message.status === 'read' && '✔✔ Read'}
                                      {message.status === 'delivered' && '✔ Delivered'}
                                      {message.status === 'sent' && '➤ Sent'}
                                      {message.status === 'received' && '➤ Received'}
                                      {message.status === 'failed' && '✖ Failed'}
                                      {!['read', 'delivered', 'sent', 'received', 'failed'].includes(message.status) && message.status}
                                    </span>
                                  </p>
                                )}

                                {/* Intent/Sentiment badges for inbound messages */}
                                {isInbound && hasIntent && (
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {message.intent && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                        Intent: {message.intent}
                                      </span>
                                    )}
                                    {message.sentiment && (
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        message.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                                        message.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {message.sentiment}
                                      </span>
                                    )}
                                    {message.confidence && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                        Confidence: {Math.round(message.confidence * 100)}%
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={`p-3 rounded border ${
                              isInbound ? 'bg-white' : 'bg-white'
                            }`}>
                              <p className="text-gray-800 whitespace-pre-wrap">{message.content || 'No content'}</p>
                              {message.mediaUrl && (
                                <div className="mt-2 pt-2 border-t">
                                  <a
                                    href={message.mediaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline"
                                  >
                                    📎 View Media
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <MessageOutlined className="text-4xl mb-4" />
                      <p>No WhatsApp messages found</p>
                      <p className="text-sm mt-2">Start a conversation to see messages here</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Email Preview Modal */}
        <Modal
          title="Email History"
          open={emailPreviewData !== null}
          onCancel={() => setEmailPreviewData(null)}
          width={900}
          footer={null}
        >
          {emailPreviewData && (
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Contact Name"
                        value={emailPreviewData.contact.contactName}
                        prefix={<UserOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="Total Emails"
                        value={emailPreviewData.emails.length}
                        prefix={<MailOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Email History</h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {emailPreviewData.emails.length > 0 ? (
                    emailPreviewData.emails.map((email, index) => {
                      // Determine read status for visual indicators
                      const isOpened = email.eventType === 'open' || email.status === 'opened';
                      const isClicked = email.eventType === 'click' || email.status === 'clicked';
                      const isDelivered = email.eventType === 'delivered' || email.status === 'delivered';
                      const isBounced = email.eventType === 'bounce' || email.status === 'bounced';

                      return (
                        <div key={index} className={`border rounded-lg p-4 hover:bg-gray-50 ${
                          isOpened ? 'border-l-4 border-l-purple-400' :
                          isDelivered ? 'border-l-4 border-l-blue-400' :
                          isBounced ? 'border-l-4 border-l-red-400' : ''
                        }`}>
                          {/* Read Receipt Banner */}
                          {(isOpened || isClicked) && (
                            <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2">
                              <span className="text-purple-600 text-lg">👁️</span>
                              <div className="flex-1">
                                <div className="font-medium text-purple-800 text-sm">
                                  {isClicked ? '🔗 Recipient clicked links in this email' : '✓ Recipient opened this email'}
                                </div>
                                <div className="text-xs text-purple-600">
                                  This means they saw your message!
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="font-semibold text-lg text-gray-800 mb-2">
                                {email.subject || 'No Subject'}
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div><strong>To:</strong> {email.recipient || 'Unknown'}</div>
                                <div><strong>From:</strong> {email.sender || 'System'}</div>
                                <div><strong>Date:</strong> {email.timestamp ? new Date(email.timestamp).toLocaleString() : 'Unknown'}</div>
                                {email.threadId && <div><strong>Thread ID:</strong> <code className="text-xs">{email.threadId}</code></div>}
                              </div>
                            </div>
                            <div className="text-xs flex flex-col gap-2">
                              <span className={`px-2 py-1 rounded-full ${
                                email.eventType === 'send_cv' ? 'bg-blue-100 text-blue-800' :
                                email.eventType === 'follow_up' ? 'bg-green-100 text-green-800' :
                                email.eventType === 'sent' ? 'bg-green-100 text-green-800' :
                                email.eventType === 'delivered' ? 'bg-blue-100 text-blue-800' :
                                email.eventType === 'opened' || email.eventType === 'open' ? 'bg-purple-100 text-purple-800' :
                                email.eventType === 'click' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {email.eventType || 'email'}
                              </span>
                            </div>
                          </div>

                        {/* Email Content Preview */}
                        {email.meta && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <h5 className="font-medium text-gray-700 mb-2">Email Details</h5>
                            <div className="text-sm text-gray-600 space-y-1">
                              {email.meta.name && <div><strong>Candidate:</strong> {email.meta.name}</div>}
                              {email.meta.title && <div><strong>Position:</strong> {email.meta.title}</div>}
                              {email.meta.email && <div><strong>Email:</strong> {email.meta.email}</div>}
                              {email.meta.phone && <div><strong>Phone:</strong> {email.meta.phone}</div>}
                            </div>
                          </div>
                        )}

                        {/* Email Body Preview */}
                        {email.content && (
                          <div className="bg-white border rounded-lg p-3">
                            <h5 className="font-medium text-gray-700 mb-2">Email Content</h5>
                            <div className="text-sm text-gray-800 max-h-64 overflow-y-auto">
                              <div className="whitespace-pre-wrap">{stripHtml(email.content)}</div>
                            </div>
                          </div>
                        )}

                        {/* Status Badge */}
                        {email.status && (
                          <div className="mt-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              email.status === 'sent' ? 'bg-green-100 text-green-800' :
                              email.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                              email.status === 'opened' ? 'bg-purple-100 text-purple-800' :
                              email.status === 'replied' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              Status: {email.status}
                            </span>
                          </div>
                        )}

                        {/* Copy Button */}
                        <div className="mt-3 flex justify-end">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              const emailText = `
Subject: ${email.subject || 'No Subject'}
To: ${email.recipient || 'Unknown'}
From: ${email.sender || 'System'}
Date: ${email.timestamp ? new Date(email.timestamp).toLocaleString() : 'Unknown'}
Status: ${email.status || 'N/A'}
Event Type: ${email.eventType || 'email'}
${email.threadId ? `Thread ID: ${email.threadId}` : ''}

${email.meta ? `
Candidate: ${email.meta.name || 'N/A'}
Position: ${email.meta.title || 'N/A'}
Email: ${email.meta.email || 'N/A'}
Phone: ${email.meta.phone || 'N/A'}
` : ''}

Content:
${stripHtml(email.content) || 'No content available'}
                              `.trim();
                              navigator.clipboard.writeText(emailText);
                              message.success('Email details copied to clipboard');
                            }}
                          >
                            Copy Email Details
                          </Button>
                        </div>
                      </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <MailOutlined className="text-4xl mb-4" />
                      <p>No email history found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </MetronicLayout>
  );
};

export default Contacts;
